import * as React from 'react';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { EventGroupType } from '../../Common/Constants/EventGroupType.Constants';
import { HealthStatus } from '../../Common/Constants/ServiceHealth.Constants';
import { ServiceHealthCDNResponse } from '../../Services/Services.Primary/HttpService/Models/ServiceHealthCDNResponse';
import { ServiceRequest } from '../../Services/Services.Primary/HttpService/Models/ServiceRequest';
import { PartnerSupportService } from '../../Services/Services.Primary/HttpService/PartnerSupportService';
import { AppSessionContext } from '../../Services/Services.Primary/SessionContext/AppSessionContext';
import TelemetryService from '../../Services/Services.Primary/Telemetry/TelemetryService';
import BrowserStorage from '../../Services/Services.Secondary/BrowserStorage';
import LocalizationService from '../../Services/Services.Secondary/Localization/LocalizationService';
import { Constants } from './Constants';
import { ServiceRequest as ServiceRequestV1 } from './Models/ServiceRequest';
import { Guid } from './Guid';
import LoadingContainer from './LoadingContainer';
import { HttpResponse } from './Models/HttpServiceModels';
import { ServiceHealth, ServiceHealthModelV1, ServiceHealthV2 } from './Models/ServiceHealth';
import { ServiceHealthFilter } from './Models/ServiceHealthFilter';
import { AdditionalInfo, Contact, FileInfo, ServiceRequestSeverity } from './Models/ServiceRequest';
import { SelfHelpSolution } from './Models/Solution';
import { SupportPanelState } from './Models/SupportPanelState';
import { Metadata, SupportTopic } from './Models/SupportTopic';
import { AllowTicketCreationInPC, ProductFamilies } from './Models/SupportTopicMetadata';
import { TopicSearchData } from './Models/TopicSearchData';
import NewFormsContainer from './NewFormsContainer';
import PageWrapperView from './PageWrapperView';
import { QueryStringParserService } from './Services/QueryStringParserService';
import SolutionsView from './SolutionsView';
import TicketDetails from './TicketDetails';
import TopicsContainer from './TopicsContainer';
import AlertBanner, { AlertType } from './Utilities/AlertBanner';
import { Helper } from './Utilities/Helper';
import WebCoreErrorBannerView, { WebCoreInformationBannerView } from './WebCoreBannerView';

let resx = LocalizationService.getInstance();
let General_Text = resx.getLocalizedResource('General_Text');
let SigninToPCToReviewSolution_text = resx.getLocalizedResource('SigninToPCToReviewSolution_text');
let ReviewSolution_text = resx.getLocalizedResource('ReviewSolution_text');
let sessionContext = AppSessionContext.getInstance();
let Constant = Constants.getInstance();
let tracingService = TelemetryService.getInstance();
let browserStorage = BrowserStorage.getInstance();
let AllPartnerCenterHelpInOnePlace_text = resx.getLocalizedResource('AllPartnerCenterHelpInOnePlace_text');
let CreateOnlineTicket_text = resx.getLocalizedResource('CreateOnlineTicket_text');
let CreateTicketInPC_text = resx.getLocalizedResource('CreateTicketInPC_text');
let WeWerentAbleToSaveYourAttachment_text = resx.getLocalizedResource('WeWerentAbleToSaveYourAttachment_text');
let TryAddAttachmentOnDetailsPage_text = resx.getLocalizedResource('TryAddAttachmentOnDetailsPage_text');
let CreateRequestsExceededError_text = resx.getLocalizedResource('CreateRequestsExceededError_text');
let CannotSubmitRequestError_text = resx.getLocalizedResource('CannotSubmitRequestError_text');
let TemporarySupportRequestBannerTitle_text = resx.getLocalizedResource('TemporarySupportRequestBannerTitle_text');
let TemporarySupportRequestBanner_text = resx.getLocalizedResource('TemporarySupportRequestBanner_text');
let TemporaryAgentSupportRequestBanner_text = resx.getLocalizedResource('TemporaryAgentSupportRequestBanner_text');
let Select_Different_Issue = resx.getLocalizedResource('Select_Different_Issue');
let ReturnToTicketList_text = resx.getLocalizedResource('ReturnToTicketList_text');

export enum CreateState {
  Solutions = 0,
  Basics = 1,
  Details = 2,
  Templates = 3,
  Confirmation = 4,
}

export type StateData = {
  topic: TopicSearchData;
  activeIncidents: ServiceHealthV2;
  ticketTrackingId: string;
  sessionId: string;
  tenantId: string;
  verbatim: string;
};

export class CategoryMap {
  public csp: string[] = new Array<string>();
  public mpn: string[] = new Array<string>();
  public incentives: string[] = new Array<string>();
  public referrals: string[] = new Array<string>();
  public isv: string[] = new Array<string>();
  public allPrograms: string[] = new Array<string>();
  public general: string[] = new Array<string>();
  public developertools: string[] = new Array<string>();
  public microsoftdynamics: string[] = new Array<string>();
  public productsupport: string[] = new Array<string>();
  public volumelicensing: string[] = new Array<string>();
}

interface ICreateTicketFlowContainerState {
  screenState: CreateState;
  loading: boolean;
  supportTopicsTree: SupportTopic[] | null;
  selectedCategoryOrWorkspace: string;
  filteredTopicsTree: SupportTopic[];
  sendingRequest: boolean;
  issueType: SupportTopic;
  issueTitle: string;
  showCreateTicketError: string | null;
  scopingSolution: SelfHelpSolution | null;
  serviceHealthInfo: ServiceHealth[] | null;
  programId: string;
  supportTopicsCategory2: string | null;
  flatTopicTree: TopicSearchData[] | null;
  workspaceTopicTree: TopicSearchData[] | null;
  topicLabel: string;
  category1To2map: CategoryMap;
  serviceRequest: ServiceRequestV1 | null;
  topicCategoriesOrWorkspaces: string[] | null;
  availableWorkspaceCategories: string[] | null;
  topicSearchAttemptCount: number;
  topicDropdownActionCount: number;
  topSupportTopicList: [SupportTopic, string][] | null;
  activeIncidentServiceHealth: Map<string, ServiceHealthV2>;
  showAttachmentError: boolean;
  isTempTicket: boolean;
  verbatim: string;
  selectedRecommendedResult: boolean;
  ticketTitle: string;
  serviceRequestsWithTopicId: ServiceRequestV1[] | null;
  workspaceDropdownEnabled: boolean;
  includeUnauthTopics: boolean;
  otherWorkspaces: { [key: string]: string };
  checkingIfShouldRedirect: boolean;
  workspaceToProductMapping: Map<string, string>;
  agentTopics: string[];
}

// base class for CreateTicketFlowContainerAuth and CreateTicketFlowContainerUnauth
export default abstract class CreateTicketFlowContainer extends React.Component<any, ICreateTicketFlowContainerState> {
  private partnerSupportService: PartnerSupportService = PartnerSupportService.getInstance();

  private L1_categories = new Set([
    Constant.ProgramId.AllProgramsId,
    Constant.ProgramId.CspProgramId,
    Constant.ProgramId.GeneralProgramId,
    Constant.ProgramId.IncentivesProgramId,
    Constant.ProgramId.IsvProgramId,
    Constant.ProgramId.MpnProgramId,
    Constant.ProgramId.ReferralsProgramId,
    Constant.ProgramId.DeveloperToolsProgramId,
    Constant.ProgramId.MicrosoftDynamicsProgramId,
    Constant.ProgramId.ProductSupportProgramId,
    Constant.ProgramId.VolumeLicensingProgramId,
  ]);
  private L15_categories = new Set([Constant.ProgramId.OemProgramId, Constant.ProgramId.SamProgramId]);
  private createStage: string = '';
  protected origQueryString: string = '';
  protected createRoute: string = '';
  protected serviceRequestRoute: string = '';
  private partnerTenantId: string = '';
  private partnerMpnId: string = '';
  private msxSellerId: string = '';
  private productFamilyId: string = '';
  protected titleClassName: string = 'col-xs-24';
  protected supportHeaderClassName: string = 'page-title-header-support-page';
  protected containerOuterClass: string = 'row flexWrapperRow';
  protected workspaceView: boolean = false;
  protected containerInnerStyle: React.CSSProperties | undefined;
  protected pageWrapperOverflowY: string = '';
  protected pageWrapperPaddingTop: string = '';
  protected isSupportPublicRoute: boolean = false;
  protected unAuthRedirect: boolean = false;
  protected supportPanel: any = null;
  protected supportContext: any = null;
  protected preventRedirect: boolean = false;
  private latestSolutionRequestTopicId: string = '';

  constructor(props: any) {
    super(props);
    initializeIcons();
    let QueryParamId = null;
    let queryParams = QueryStringParserService.getQueryParameters(window.location.search);
    if (
      queryParams['category'] &&
      (queryParams['category'] === Constant.ProgramId.CspProgramId ||
        queryParams['category'] === Constant.ProgramId.MpnProgramId ||
        queryParams['category'] === Constant.ProgramId.IsvProgramId ||
        queryParams['category'] === Constant.ProgramId.ReferralsProgramId ||
        queryParams['category'] === Constant.ProgramId.IncentivesProgramId)
    ) {
      QueryParamId = queryParams['category'];
    } else if (sessionContext.VirtualDirectoryRoot.includes('/${Constant.ProgramId.CspProgramId}/')) {
      QueryParamId = Constant.ProgramId.CspProgramId;
    } else if (sessionContext.VirtualDirectoryRoot.includes('/${Constant.ProgramId.MpnProgramId}/')) {
      QueryParamId = Constant.ProgramId.MpnProgramId;
    } else if (sessionContext.VirtualDirectoryRoot.includes('/${Constant.ProgramId.ReferralsProgramId}/')) {
      QueryParamId = Constant.ProgramId.ReferralsProgramId;
    } else if (sessionContext.VirtualDirectoryRoot.includes('/${Constant.ProgramId.IsvProgramId}/')) {
      QueryParamId = Constant.ProgramId.IsvProgramId;
    }

    if (
      queryParams[Constant.QueryStringParams.isPreview] &&
      queryParams[Constant.QueryStringParams.isPreview].toLowerCase() === 'true'
    ) {
      this.origQueryString = '&isPreview=true';
    }
    if (queryParams['unauthRedirect'] && queryParams['unauthRedirect'].toLowerCase() === 'true') {
      this.unAuthRedirect = true;
    }
    if (queryParams[Constant.QueryStringParams.isAzPreview]) {
      this.origQueryString +=
        '&' +
        Constant.QueryStringParams.isAzPreview +
        '=' +
        queryParams[Constant.QueryStringParams.isAzPreview].toLowerCase();
    }
    if (QueryStringParserService.getValue(Constant.QueryStringParams.IsSdPreview) == 'true') {
      this.origQueryString += '&IsSdPreview=true';
    }
    if (queryParams['trailBlazerTestOnly'] && queryParams['trailBlazerTestOnly'].toLowerCase() === 'true') {
      this.origQueryString += '&trailBlazerTestOnly=true';
    }
    if (queryParams['testTopics'] && queryParams['testTopics'].toLowerCase() === 'true') {
      this.origQueryString += '&testTopics=true';
    }
    if (
      queryParams[Constant.QueryStringParams.refreshed] &&
      queryParams[Constant.QueryStringParams.refreshed].toLowerCase() === 'true'
    ) {
      this.origQueryString += '&' + Constant.QueryStringParams.refreshed + '=true';
    }
    if (
      queryParams[Constant.QueryStringParams.preventRedirect] &&
      queryParams[Constant.QueryStringParams.preventRedirect].toLowerCase() === 'true'
    ) {
      this.preventRedirect = true;
    }
    this.createStage = 's1';
    let screenState = CreateState.Basics;
    if (queryParams['stage'] && queryParams['stage'] === '2') {
      screenState = CreateState.Solutions;
      this.createStage = 's2';
    } else if (queryParams['stage'] && queryParams['stage'] === '3') {
      screenState = CreateState.Details;
      this.createStage = 's3';
    }

    if (
      window.location.pathname.indexOf('internal/tools/support') !== -1 &&
      window.location.pathname.indexOf('seller') !== -1
    ) {
      this.createRoute = Constant.Routes.ServiceRequestsCreateMsxSeller;
      this.serviceRequestRoute = Constant.Routes.ServiceRequestsMsxSeller;
      this.productFamilyId = ProductFamilies.MSX;

      if (queryParams['msxsellerid'] && queryParams['msxsellerid'].length > 0) {
        this.msxSellerId = queryParams['msxsellerid'];
        this.origQueryString += '&msxsellerid=' + queryParams['msxsellerid'];
      }
    } else {
      this.createRoute = Constant.Routes.ServiceRequestsCreate;
      this.serviceRequestRoute = Constant.Routes.ServiceRequests;
      this.productFamilyId = ProductFamilies.PartnerCenter;

      if (queryParams['tid'] && queryParams['tid'].length > 0) {
        this.partnerTenantId = queryParams['tid'];
        this.origQueryString += '&tid=' + queryParams['tid'];
      }
      if (queryParams['mpnid'] && queryParams['mpnid'].length > 0) {
        this.partnerMpnId = queryParams['mpnid'];
        this.origQueryString += '&mpnid=' + queryParams['mpnid'];
      }
    }

    if (queryParams['ticketTrackingId'] && queryParams['ticketTrackingId'].length > 0) {
      sessionContext.TicketTrackingId = queryParams['ticketTrackingId'];
    } else {
      sessionContext.resetTicketTrackingId();
      let eventName = Constant.actionTypes.newRequest;
      tracingService.traceEvent(
        eventName,
        { info: Constant.actionTypes.unAuthNewRequestEvent },
        EventGroupType.ticketCreation
      );
    }

    let verbatim = '';
    if (queryParams['verbatim'] && queryParams['verbatim'].length > 0) {
      verbatim = queryParams['verbatim'];
    }

    this.state = {
      screenState: screenState,
      loading: true,
      supportTopicsTree: null,
      selectedCategoryOrWorkspace: '',
      filteredTopicsTree: [],
      sendingRequest: false,
      issueType: {
        id: '',
        name: '',
        description: '',
        subtopics: [],
        categories: [],
        metadata: {
          authenticationStep: [],
          allowTicketCreationInPC: [],
          ticketingSystem: [],
          allowGuestTicketCreationInPC: [],
          searchTags: [],
          workspace: [],
          workspaceCategory: [],
          wayfinderModelId: [],
          partCConnectorPaths: [],
          enabledSupportOptionsInPC: [],
          categories: [],
          categoriesLevel2: [],
          seamId: [],
        },
      },
      issueTitle: '',
      showCreateTicketError: null,
      scopingSolution: null,
      serviceHealthInfo: null,
      programId: QueryParamId,
      supportTopicsCategory2: null,
      flatTopicTree: null,
      workspaceTopicTree: null,
      topicLabel: '',
      category1To2map: new CategoryMap(),
      serviceRequest: null,
      topicCategoriesOrWorkspaces: null,
      availableWorkspaceCategories: null,
      topicSearchAttemptCount: 0,
      topicDropdownActionCount: 0,
      topSupportTopicList: null,
      activeIncidentServiceHealth: new Map<string, ServiceHealthV2>(),
      showAttachmentError: false,
      isTempTicket: false,
      verbatim: verbatim, // if query string contains verbatim, then set verbatim to the query string. It is used to preserve verbatim when user redirect from unauth page to auth page
      selectedRecommendedResult: false,
      ticketTitle: '', // this state is used to prepopulate verbatim on search page if user want to select a different topic on create page
      serviceRequestsWithTopicId: null,
      workspaceDropdownEnabled: false,
      includeUnauthTopics: true,
      otherWorkspaces: {},
      checkingIfShouldRedirect: false,
      workspaceToProductMapping: new Map<string, string>(),
      agentTopics: [],
    };
  }

  async componentDidMount() {
    await this.getInitializationData();
  }

  componentDidUpdate() {
    window.onpopstate = async e => {
      if (this.state.screenState === CreateState.Details) {
        this.setSolutionsState(this.state.issueType);
      } else if (this.state.screenState === CreateState.Solutions) {
        this.setBasicsState();
      }
    };
  }

  abstract setBasicsRoute: () => void;

  abstract setSolutionRoute: (issueType: SupportTopic) => void;

  abstract setDetailsRoute: (issueType: SupportTopic) => void;

  abstract setConfirmationRoute: () => void;

  abstract cancel: () => void;

  abstract getSolutionBackButton: () => JSX.Element;

  abstract getSolutionNextStepDiv: (
    issueType: SupportTopic,
    serviceRequestsWithTopicId: ServiceRequestV1[]
  ) => JSX.Element;

  abstract blockPcCreationByProgramClaim: () => boolean;

  abstract getProvideDetailsText: () => string;

  abstract setDetailsState: (issueType: SupportTopic) => Promise<void>;

  protected isStepRequireAuth = (step: string, issueType: SupportTopic) => {
    return (
      issueType.metadata &&
      issueType.metadata.authenticationStep &&
      issueType.metadata.authenticationStep.indexOf(step) != -1 &&
      window.location.pathname.indexOf('dashboard') === -1 &&
      window.location.pathname.indexOf('internal/tools/support') === -1
    );
  };

  protected isContinueDisabled = (): boolean => {
    return (
      (this.state.issueType.metadata &&
        this.state.issueType.metadata.allowTicketCreationInPC &&
        this.state.issueType.metadata.allowTicketCreationInPC.indexOf('Blocked') != -1) ||
      this.shouldBlockCreation() ||
      this.blockPcCreationByProgramClaim()
    );
  };

  protected setDetailQueryString = (): void => {};

  //setting  Details Stage if redirecte from MSA
  private setDetailsStageFromMsa = async () => {
    let queryParams = QueryStringParserService.getQueryParameters(window.location.search);
    let QueryStringsList = queryParams['MsaRedirect'].split('-');
    let stage = QueryStringsList[0];
    let topicId = QueryStringsList[1];
    if (stage && topicId === '3') {
      this.createStage = 's3';
    }
    await this.setTopicStateById(topicId);
  };

  protected getVerbatimQueryString = (): string => {
    let verbatimQueryString = '';
    if (!Helper.isEmptyOrSpaces(this.state.verbatim)) {
      verbatimQueryString = '&verbatim=' + this.state.verbatim;
    }
    return verbatimQueryString;
  };

  private selectTopic = (
    issueType: SupportTopic,
    label: string,
    verbatim: string = this.state.verbatim,
    selectedRecommendedResult: boolean = false,
    fromSearchResult: boolean = true
  ) => {
    let eventName = Constant.actionTypes.selectSupportTopic;
    verbatim = verbatim === null ? this.state.verbatim : verbatim;
    let message = {
      verbatim: verbatim,
      selectedRecommendedResult: selectedRecommendedResult,
      supportTopicId: issueType.id.toString(),
      supportTopicName: label,
      fromSearchResult: fromSearchResult,
    };
    tracingService.traceEvent(eventName, message, EventGroupType.userInteraction);

    this.setState({
      issueType: issueType,
      issueTitle: this.getParentTopicNameById(issueType.id) ?? '',
      topicLabel: label,
      scopingSolution: null,
      verbatim: verbatim,
      selectedRecommendedResult: selectedRecommendedResult,
    });
  };

  private setSolutionsState = async (issueType: SupportTopic = this.state.issueType) => {
    if (this.isStepRequireAuth('Solution', issueType)) {
      Helper.setPublicDeepLinkInfo(
        issueType.id,
        SupportPanelState.solution,
        this.state.verbatim,
        sessionContext.TicketTrackingId,
        browserStorage,
        tracingService
      );

      let newUrl =
        window.location.origin +
        '/dashboard/v2/support/' +
        this.createRoute +
        '?stage=2&topicid=' +
        issueType.id +
        this.origQueryString +
        '&ticketTrackingId=' +
        sessionContext.TicketTrackingId +
        this.getVerbatimQueryString();

      tracingService.traceEvent(Constant.actionTypes.unAuthRedirectToDashboardEvent, {
        newUrl: newUrl,
        issueType: issueType,
      });

      window.location.href = newUrl;
    } else {
      if (this.state.scopingSolution == null) {
        this.setState({ loading: true, scopingSolution: null });
        await this.LoadScopingSolutions(issueType);
      } else {
        this.setSolutionRoute(issueType);
        this.setState({
          screenState: CreateState.Solutions,
          issueType: issueType,
          issueTitle: this.getParentTopicNameById(issueType.id) ?? '',
          loading: false,
        });
      }
    }
  };

  private loadScopingSolutionsCallback = async (issueType: SupportTopic = this.state.issueType) => {
    if (this.state.scopingSolution != null) {
      this.setSolutionRoute(issueType);
      this.setState({
        screenState: CreateState.Solutions,
        issueType: issueType,
        issueTitle: this.getParentTopicNameById(issueType.id) ?? '',
        loading: false,
      });
      tracingService.traceEvent(Constant.actionTypes.unAuthSolutionFoundEvent, {
        azureSupportSolution: this.state.scopingSolution,
      });
    } else {
      //Checks if topic is supposed to be enabled for ticket creation so in case there's no solution allow them to go to details
      let topicTree = this.state.workspaceDropdownEnabled ? this.state.flatTopicTree : this.state.workspaceTopicTree;
      if (topicTree && topicTree.find(x => x.topicId == issueType.id)) {
        await this.setDetailsState(issueType);
        tracingService.traceEvent(Constant.actionTypes.unAuthSolutionNotFoundNavigateToDetailsEvent, {
          issueType: issueType,
        });
      } else {
        this.setBasicsState();
        tracingService.traceEvent(Constant.actionTypes.unAuthSolutionNotFoundNavigateToBasicEvent, {
          issueType: issueType,
        });
      }
    }
  };

  protected setBasicsState = () => {
    this.latestSolutionRequestTopicId = '';
    this.setBasicsRoute();
    // Looks like there is some race condition on resetting the scopingSolution on selection/reselection of topic only from the top topics view.
    // So resetting it on basic view. TODO: revisit this.
    this.setState({
      screenState: CreateState.Basics,
      issueType: {
        id: '',
        name: '',
        description: '',
        subtopics: [],
        categories: [],
        metadata: {
          authenticationStep: [],
          allowTicketCreationInPC: [],
          ticketingSystem: [],
          allowGuestTicketCreationInPC: [],
          searchTags: [],
          workspace: [],
          workspaceCategory: [],
          wayfinderModelId: [],
          partCConnectorPaths: [],
          enabledSupportOptionsInPC: [],
          categories: [],
          categoriesLevel2: [],
          seamId: [],
        },
      },
      showCreateTicketError: '',
      scopingSolution: null,
      showAttachmentError: false,
      verbatim: '',
      selectedCategoryOrWorkspace: '',
    });

    tracingService.traceEvent(Constant.actionTypes.unAuthSetBasicStateEvent);
  };

  private setSupportTopicsWorkspace = (workspaceId: string) => {
    const topics = this.state.supportTopicsTree?.filter((topic: SupportTopic) => {
      if (topic && topic.metadata) {
        return (
          Helper.isEmptyOrSpaces(workspaceId) ||
          (topic.metadata[Constant.Workspace.WorkspaceProperty as keyof Metadata] ?? []).includes(workspaceId) ||
          (topic.metadata[Constant.Workspace.WorkspaceCategoryProperty as keyof Metadata] ?? []).includes(
            workspaceId
          ) ||
          topic.id == workspaceId
        );
      }
    });

    this.setState({
      selectedCategoryOrWorkspace: workspaceId,
      filteredTopicsTree: topics ?? [],
    });
  };

  private setSupportTopicsCategory = (programId: string) => {
    this.setState({ selectedCategoryOrWorkspace: programId, supportTopicsCategory2: null });
  };

  private setSupportTopicsCategory2 = (programId: string) => {
    this.setState({ supportTopicsCategory2: programId });
  };

  private getInitializationData = async () => {
    this.setState({
      loading: true,
      workspaceDropdownEnabled: true,
    });
    await this.getSupportTopicsTree();
    await this.getServiceHealth();
    let queryParams = QueryStringParserService.getQueryParameters(window.location.search);
    //setting state for topic redirected MSA with topicID and Stage
    if (queryParams['MsaRedirect']) {
      this.setDetailsStageFromMsa();
    } else {
      await this.setTopicStateById(queryParams[Constant.QueryStringParams.topicid]);
    }

    if (this.state.scopingSolution == null && this.state.issueType && !Helper.isEmptyOrSpaces(this.state.issueType.id)) {
      await this.LoadScopingSolutions(this.state.issueType);
    }

    if (this.state.workspaceDropdownEnabled) {
      this.setFlattenedTopicTreeAndTopSupportTopicsByWorkspace();
    } else {
      // If category based topic tree set workspaceTopicTree for redirection.
      this.setFlattenedTopicTreeAndTopSupportTopicsByCategory();
      this.getFlattenedTopicTreeAndTopSupportTopicsByWorkspace((flatTree: any, _topSupportTopics: any) =>
        this.setState({ workspaceTopicTree: flatTree })
      );
    }

    tracingService.traceEvent(Constant.actionTypes.unAuthLoadSuccessEvent, {
      supportTopics: this.state.supportTopicsTree,
      serviceHealth: this.state.activeIncidentServiceHealth,
    });
    this.setState({ loading: false });
  };

  private setFlattenedTopicTreeAndTopSupportTopicsByWorkspace = () => {
    this.getFlattenedTopicTreeAndTopSupportTopicsByWorkspace((flatTree: any, topSupportTopics: any) =>
      this.setState({ flatTopicTree: flatTree, topSupportTopicList: topSupportTopics })
    );
  };

  private setTopicStateById = async (topicid: string) => {
    if (this.state.supportTopicsTree && !Helper.isEmptyOrSpaces(topicid)) {
      let topicIdIssueType: SupportTopic | undefined;
      let eventName: string = '';
      // Finding Sap Id  by seam Id & Back compatibility
      if (Number(topicid)) {
        //Finding Topic Id in Leaf Tree
        if (this.state.supportTopicsTree) {
          topicIdIssueType = this.state.supportTopicsTree
            .map(x => x.subtopics ?? [])
            .filter(y => y.length > 0)
            .reduce((acc, val) => acc.concat(val), [])
            .find(x => (x.metadata?.seamId ?? []).indexOf(topicid) > -1);
        }

        if (!topicIdIssueType) {
          //Finding Topic Id in L2 Tree
          topicIdIssueType = this.state.supportTopicsTree.find(x => (x.metadata?.seamId ?? []).indexOf(topicid) > -1);
        }
        // if valid topicId, create SEAM actionTag for logging
        if (topicIdIssueType) {
          eventName = Constant.actionTypes.SolutionsDeepLinkBySeamId;
        }
      } else if (this.createStage === 's2' || this.createStage === 's3') {
        //Finding Topic Id in Leaf Tree
        topicIdIssueType = this.state.supportTopicsTree
          .map(x => x.subtopics ?? [])
          .filter(y => y.length > 0)
          .reduce((acc, val) => acc.concat(val), [])
          .find(z => z.id == topicid);
        if (!topicIdIssueType) {
          //Finding Topic Id in L2 Tree
          topicIdIssueType = this.state.supportTopicsTree.find(x => x.id == topicid);
        }
        // if valid topicId, create SAP actionTag for logging
        if (topicIdIssueType) {
          eventName = Constant.actionTypes.SolutionsDeepLinkBySapId;
        }
      }
      if (topicIdIssueType) {
        let action = { actionType: Constant.TraceTypes.TrackEvent, tags: eventName };
        tracingService.traceInformation('Setting create state based on query params', action);
        if (
          this.createStage === 's3' &&
          QueryStringParserService.getValue(Constant.QueryStringParams.unauthRedirect) == 'true'
        ) {
          await this.setDetailsState(topicIdIssueType);
        } else {
          if (this.isStepRequireAuth('Solution', topicIdIssueType)) {
            this.setBasicsState();
          } else {
            await this.setSolutionsState(topicIdIssueType);
          }
        }
      } else {
        this.setBasicsState();
      }
    } else {
      this.setBasicsState();
    }
  };

  protected getParentTopicNameById = (topicId: string) => {
    //Finding Topic Name for L1 Tree
    let supportTopic = (this.state.supportTopicsTree ?? []).find(supportTopic => supportTopic.id == topicId);
    if (supportTopic) {
      return General_Text;
    }
    //Finding Topic Name for Leaf Tree
    if (this.state.supportTopicsTree) {
      for (let i = 0; i < this.state.supportTopicsTree.length; i++) {
        if (this.state.supportTopicsTree[i].subtopics != null) {
          let st = (this.state.supportTopicsTree[i].subtopics ?? []).find(x => x.id == topicId);
          if (st) {
            return this.state.supportTopicsTree[i].name;
          }
        }
      }
    }
  };

  public setClearSearch = () => {
    this.setState({
      issueType: {
        id: '',
        name: '',
        description: '',
        subtopics: [],
        categories: [],
        metadata: {
          authenticationStep: [],
          allowTicketCreationInPC: [],
          ticketingSystem: [],
          categories: [],
          categoriesLevel2: [],
          allowGuestTicketCreationInPC: [],
          enabledSupportOptionsInPC: [],
          workspace: [],
          workspaceCategory: [],
          seamId: [],
          searchTags: [],
          wayfinderModelId: [],
          partCConnectorPaths: [],
        },
      },
      issueTitle: '',
      topicLabel: '',
      selectedCategoryOrWorkspace: '',
      supportTopicsCategory2: null,
    });
  };

  public incrementTopicSearchAttemptCount = () => {
    let count = this.state.topicSearchAttemptCount + 1;
    this.setState({ topicSearchAttemptCount: count });
  };

  public incrementTopicDropdownActionCount = () => {
    let count = this.state.topicDropdownActionCount + 1;
    this.setState({ topicDropdownActionCount: count });
  };

  private alphabetizeByTopicName = (topics: SupportTopic[]): SupportTopic[] => {
    //sort topics by name alphabetically
    if (!topics || topics.length < 2) {
      return topics;
    }

    let sortedTopicsList: SupportTopic[] = topics.sort((a, b) => {
      if (a.name > b.name) return 1;
      if (a.name <= b.name) return -1;

      return 0;
    });

    return sortedTopicsList;
  };

  private sortCategoriesOrWorkspaces = (categoriesOrWorkspaces: string[]): string[] => {
    return categoriesOrWorkspaces.sort((a, b) =>
      this.state.workspaceDropdownEnabled
        ? Helper.getWorkspaceTagFromId(a).localeCompare(Helper.getWorkspaceTagFromId(b))
        : Helper.getProgramTagFromId(a).localeCompare(Helper.getProgramTagFromId(b))
    );
  };

  private getSupportTopicsTree = async () => {
    const topicCategoriesOrWorkspacesFound = new Set<string>();
    const availableWorkspaceCategories = new Set<string>();
    let otherWorkspace: { [key: string]: string } = {};
    let agentTopic = new Set<string>();

    let stateChanges: {
      supportTopicsTree: SupportTopic[];
      topicCategoriesOrWorkspaces: string[];
    } = { supportTopicsTree: [], topicCategoriesOrWorkspaces: [] };

    let supportTopicsCDNResponse = await this.partnerSupportService.getSupportTopics();

    if (supportTopicsCDNResponse && supportTopicsCDNResponse.data.Data.length > 0) {
      const supportTopics = supportTopicsCDNResponse.data.Data as unknown as SupportTopic[];

      supportTopics.forEach(topic => {
        if (this.state.workspaceDropdownEnabled) {
          const metadataProperty = this.getMetadataProperty(topic);
          if (metadataProperty === '') return;

          topic.metadata[metadataProperty as keyof Metadata].forEach(workspaceL2 =>
            metadataProperty === Constant.Workspace.WorkspaceCategoryProperty
              ? this.matchWayfinderModelIds(topic.metadata.wayfinderModelId, Constant.WayfinderModelIds.OtherPortalsId)
                ? (otherWorkspace[topic.id] = workspaceL2)
                : this.matchWayfinderModelIds(
                    topic.metadata.wayfinderModelId,
                    Object.keys(Constant.WayfinderModelIds.AgentFlowIds)
                  )
                ? agentTopic.add(workspaceL2)
                : availableWorkspaceCategories.add(workspaceL2)
              : topicCategoriesOrWorkspacesFound.add(workspaceL2)
          );

          if (topic.subtopics.length >= 1) {
            topic.subtopics.forEach(subtopic => {
              subtopic.metadata[metadataProperty as keyof Metadata].forEach(workspaceL3 =>
                metadataProperty === Constant.Workspace.WorkspaceCategoryProperty
                  ? this.matchWayfinderModelIds(
                      topic.metadata.wayfinderModelId,
                      Constant.WayfinderModelIds.OtherPortalsId
                    )
                    ? (otherWorkspace[topic.id] = workspaceL3)
                    : this.matchWayfinderModelIds(
                        topic.metadata.wayfinderModelId,
                        Object.keys(Constant.WayfinderModelIds.AgentFlowIds)
                      )
                    ? agentTopic.add(workspaceL3)
                    : availableWorkspaceCategories.add(workspaceL3)
                  : topicCategoriesOrWorkspacesFound.add(workspaceL3)
              );
            });
          }
        } else {
          //if topic has no categories, file it as general
          if (!topic.categories || topic.categories.length === 0) {
            topic.categories = [Constant.ProgramId.GeneralProgramId];
          }

          //if no subtopics, then it is a leafttopic and should have only 1 category
          if (topic.subtopics.length < 1 && this.L1_categories.has(topic.categories[0])) {
            topicCategoriesOrWorkspacesFound.add(topic.categories[0]);
          }

          topic.subtopics.forEach(subtopic => {
            if (!subtopic.categories || subtopic.categories.length === 0) {
              subtopic.categories = [Constant.ProgramId.GeneralProgramId];
            }

            //subtopics should not have more than 1 category
            if (this.L1_categories.has(subtopic.categories[0])) {
              topicCategoriesOrWorkspacesFound.add(subtopic.categories[0]);
            }
          });

          if (topic.subtopics) {
            topic.subtopics = this.alphabetizeByTopicName(topic.subtopics);
          }
        }
      });

      stateChanges.supportTopicsTree = this.alphabetizeByTopicName(supportTopics);
    } else {
      stateChanges.supportTopicsTree = [];
    }

    stateChanges.topicCategoriesOrWorkspaces = this.sortCategoriesOrWorkspaces([
      ...Array.from(topicCategoriesOrWorkspacesFound),
      ...Array.from(availableWorkspaceCategories),
    ]);

    if (this.state.workspaceDropdownEnabled) {
      this.setState({
        ...stateChanges,
        selectedCategoryOrWorkspace: '',
        availableWorkspaceCategories: Array.from(availableWorkspaceCategories),
        otherWorkspaces: otherWorkspace,
        agentTopics: Array.from(agentTopic),
      });
    } else {
      this.setState(stateChanges);
    }
  };

  private matchWayfinderModelIds = (wayfinderModelIds: string[], match: string[]): boolean => {
    if (!wayfinderModelIds.length || !match.length) {
      return false;
    }

    return match.indexOf(wayfinderModelIds[0]) !== -1;
  };

  private getFlattenedTopicTreeAndTopSupportTopicsByWorkspace = (callbackToSetTree: Function) => {
    let flatTree = new Array<TopicSearchData>();
    let topSupportTopics: any[] = [];
    let topTopics: string[] = Constant.TopSupportTopics;

    let queryParams = QueryStringParserService.getQueryParameters(window.location.search);
    let queryTopicLabel = '';

    (this.state.supportTopicsTree ?? []).forEach(supportTopic => {
      const metadataProperty = this.getMetadataProperty(supportTopic);
      if (metadataProperty === '') return;

      const additionalCategoryTags: any[] = [];
      if (supportTopic.metadata !== undefined) {
        (supportTopic.metadata[metadataProperty as keyof Metadata] ?? []).forEach(workspaceL2 => {
          if (supportTopic && supportTopic.subtopics && supportTopic.subtopics.length < 1) {
            const topicLabel = Helper.getWorkspaceTagFromId(workspaceL2) + ' > ' + supportTopic.name;

            if (
              queryParams[Constant.QueryStringParams.topicid] &&
              supportTopic.id.toString() === queryParams[Constant.QueryStringParams.topicid]
            ) {
              queryTopicLabel = topicLabel;
            }

            const searchTagSet = new Set([
              Helper.cleanPunctuation(Helper.getWorkspaceTagFromId(workspaceL2).toLowerCase()),
              Helper.cleanPunctuation(supportTopic.name.toLowerCase()),
            ]);

            const additionalTags = additionalCategoryTags.concat(supportTopic.metadata?.searchTags);
            additionalTags.forEach(tag => {
              searchTagSet.add(Helper.cleanPunctuation(tag.toLowerCase()));
            });

            if (supportTopic.metadata?.wayfinderModelId && supportTopic.metadata.wayfinderModelId.length > 0) {
              const productId = supportTopic.metadata.wayfinderModelId[0];
              this.addToWorkspaceToProductMapping(workspaceL2, productId);
            }

            flatTree.push(
              Object.assign({} as TopicSearchData, {
                label: topicLabel,
                searchTags: searchTagSet,
                topicId: supportTopic.id,
                workspaceId: workspaceL2,
                workspaceName: Helper.getWorkspaceTagFromId(workspaceL2),
                L2: supportTopic,
                L3: null,
              })
            );

            // insert into top topic if topic found in top topics list
            if (topTopics.indexOf(supportTopic.id) != -1) {
              topSupportTopics.push([supportTopic, topicLabel]);
            }
          } else {
            supportTopic.subtopics?.forEach(subtopic => {
              if (subtopic && subtopic?.metadata) {
                (subtopic?.metadata[metadataProperty as keyof Metadata] ?? []).forEach(workspaceL3 => {
                  const topicLabel =
                    Helper.getWorkspaceTagFromId(workspaceL3) + ' > ' + supportTopic.name + ' > ' + subtopic.name;

                  if (
                    queryParams[Constant.QueryStringParams.topicid] &&
                    subtopic.id.toString() === queryParams[Constant.QueryStringParams.topicid]
                  ) {
                    queryTopicLabel = topicLabel;
                  }

                  const searchTagSet = new Set([
                    Helper.cleanPunctuation(Helper.getWorkspaceTagFromId(workspaceL3).toLowerCase()),
                    Helper.cleanPunctuation(supportTopic.name.toLowerCase()),
                    Helper.cleanPunctuation(subtopic.name.toLowerCase()),
                  ]);

                  const additionalTags = additionalCategoryTags.concat(subtopic.metadata?.searchTags);
                  additionalTags.forEach(tag => {
                    searchTagSet.add(Helper.cleanPunctuation(tag.toLowerCase()));
                  });

                  if (supportTopic.metadata?.wayfinderModelId && supportTopic.metadata.wayfinderModelId.length > 0) {
                    const productId = supportTopic.metadata.wayfinderModelId[0];
                    this.addToWorkspaceToProductMapping(workspaceL3, productId);
                  }

                  flatTree.push(
                    Object.assign({} as TopicSearchData, {
                      label: topicLabel,
                      searchTags: searchTagSet,
                      topicId: subtopic.id,
                      workspaceId: workspaceL3,
                      workspaceName: Helper.getWorkspaceTagFromId(workspaceL3),
                      L2: supportTopic,
                      L3: subtopic,
                    })
                  );

                  // insert into top topic if topic found in top topics list
                  if (topTopics.indexOf(subtopic.id) != -1) {
                    topSupportTopics.push([subtopic, topicLabel]);
                  }
                });
              }
            });
          }
        });
      }
    });
    if (!Helper.isEmptyOrSpaces(queryTopicLabel)) {
      this.setState({ topicLabel: queryTopicLabel });
    }

    topSupportTopics = topSupportTopics.sort(Helper.topTopicDisplaySortHelper);
    callbackToSetTree(flatTree, topSupportTopics);
  };

  private setFlattenedTopicTreeAndTopSupportTopicsByCategory = () => {
    let flatTree = new Array<TopicSearchData>();
    let topSupportTopics: any[] = [];
    let topTopics: string[] = [];

    let queryParams = QueryStringParserService.getQueryParameters(window.location.search);
    let queryTopicLabel = '';
    let category1To2map = this.state.category1To2map;
    let categoriesContainingTopicsWithoutCat2 = new Set<string>();
    if (this.state.supportTopicsTree) {
      this.state.supportTopicsTree.forEach(supportTopic => {
        (supportTopic.categories ?? []).forEach(category => {
          if (this.L1_categories.has(category)) {
            let l15_label = '';
            let l15_textMatch = '';
            let category2 = '';
            let additionalCategoryTags: any[] = [];
            if (
              supportTopic.metadata &&
              supportTopic.metadata.categoriesLevel2 &&
              supportTopic.metadata.categoriesLevel2.length > 0
            ) {
              supportTopic.metadata.categoriesLevel2.forEach(categoryl2 => {
                if (category2.length > 0) {
                  //currently  only 1 cat2 is supported per topic so if there's more than 1 it is added as a tag so the topic is stil searchable by that term
                  additionalCategoryTags.push(categoryl2);
                } else if (this.L15_categories.has(categoryl2)) {
                  if (category1To2map[category as keyof CategoryMap].indexOf(categoryl2) === -1) {
                    category1To2map[category as keyof CategoryMap].push(categoryl2);
                  }
                  l15_label = ' > ' + Helper.getProgramTagFromId(categoryl2);
                  l15_textMatch = ' ' + Helper.getProgramTagFromId(categoryl2);
                  category2 = categoryl2;
                }
              });
            } else {
              if (!categoriesContainingTopicsWithoutCat2.has(category)) {
                categoriesContainingTopicsWithoutCat2.add(category);
              }
            }
            if (supportTopic && supportTopic.subtopics && supportTopic.subtopics.length < 1) {
              let topicLabel = Helper.getProgramTagFromId(category) + l15_label + ' > ' + supportTopic.name;
              if (
                queryParams[Constant.QueryStringParams.topicid] &&
                supportTopic.id.toString() === queryParams[Constant.QueryStringParams.topicid]
              ) {
                queryTopicLabel = topicLabel;
              }

              let searchTagSet = new Set<string>([
                Helper.getProgramTagFromId(category).toLowerCase(),
                Helper.cleanPunctuation(l15_textMatch.toLowerCase()),
                Helper.cleanPunctuation(supportTopic.name.toLowerCase()),
              ]);
              let additionalTags = additionalCategoryTags.concat(supportTopic.metadata?.searchTags);
              additionalTags.forEach(tag => {
                searchTagSet.add(Helper.cleanPunctuation(tag.toLowerCase()));
              });

              flatTree.push(
                Object.assign({} as TopicSearchData, {
                  label: topicLabel,
                  searchTags: searchTagSet,
                  topicId: supportTopic.id,
                  category: category,
                  category2: category2,
                  L2: supportTopic,
                  L3: null,
                })
              );

              // insert into top topic if topic found in top topics list
              if (topTopics.indexOf(supportTopic.id) != -1) {
                topSupportTopics.push([supportTopic, topicLabel]);
              }
            } else {
              if (supportTopic.subtopics) {
                supportTopic.subtopics.forEach(subtopic => {
                  //each subtopic should only have one category, hence the choosing the first item
                  if (subtopic.categories && subtopic.categories[0].toString() === category) {
                    let topicLabel =
                      Helper.getProgramTagFromId(category) +
                      l15_label +
                      ' > ' +
                      supportTopic.name +
                      ' > ' +
                      subtopic.name;
                    if (
                      queryParams[Constant.QueryStringParams.topicid] &&
                      subtopic.id.toString() === queryParams[Constant.QueryStringParams.topicid]
                    ) {
                      queryTopicLabel = topicLabel;
                    }

                    let searchTagSet = new Set<string>([
                      Helper.cleanPunctuation(Helper.getProgramTagFromId(category).toLowerCase()),
                      Helper.cleanPunctuation(l15_textMatch.toLowerCase()),
                      Helper.cleanPunctuation(supportTopic.name.toLowerCase()),
                      Helper.cleanPunctuation(subtopic.name.toLowerCase()),
                    ]);

                    let additionalTags = additionalCategoryTags.concat(subtopic.metadata?.searchTags);
                    additionalTags.forEach(tag => {
                      searchTagSet.add(Helper.cleanPunctuation(tag.toLowerCase()));
                    });

                    flatTree.push(
                      Object.assign({} as TopicSearchData, {
                        label: topicLabel,
                        searchTags: searchTagSet,
                        topicId: subtopic.id,
                        category: category,
                        category2: category2,
                        L2: supportTopic,
                        L3: subtopic,
                      })
                    );

                    // insert into top topic if topic found in top topics list
                    if (topTopics.indexOf(subtopic.id) != -1) {
                      topSupportTopics.push([subtopic, topicLabel]);
                    }
                  }
                });
              }
            }
          }
        });
      });
    }

    if (!Helper.isEmptyOrSpaces(queryTopicLabel)) {
      this.setState({ topicLabel: queryTopicLabel });
    }

    //if there are l1 categories that have l1.5 categories for some but not all of their topics, add 'other' option to map so there is a label to find them in the l1.5 dropdown
    categoriesContainingTopicsWithoutCat2.forEach(category => {
      if (category1To2map[category as keyof CategoryMap].length > 0) {
        if (category1To2map[category as keyof CategoryMap].indexOf(Constant.ProgramId.OtherProgram) === -1) {
          category1To2map[category as keyof CategoryMap].push(Constant.ProgramId.OtherProgram);
        }
      }
    });

    topSupportTopics = topSupportTopics.sort(Helper.topTopicDisplaySortHelper);
    this.setState({ flatTopicTree: flatTree, category1To2map: category1To2map, topSupportTopicList: topSupportTopics });
  };

  private getMetadataProperty = (topic: SupportTopic): string => {
    if (
      topic.metadata &&
      topic.metadata.workspace &&
      topic.metadata.workspace.length > 0 &&
      topic.metadata.workspace[0] !== Constant.Workspace.None
    ) {
      return Constant.Workspace.WorkspaceProperty;
    } else if (
      topic.metadata &&
      topic.metadata.workspaceCategory &&
      topic.metadata.workspaceCategory.length > 0 &&
      topic.metadata.workspaceCategory[0] !== Constant.Workspace.None
    ) {
      return Constant.Workspace.WorkspaceCategoryProperty;
    } else if (this.state.includeUnauthTopics) {
      if (topic.subtopics && topic.subtopics.length < 1) {
        const unauthTopic = this.isUnauthTopic(topic);
        if (unauthTopic) {
          if (topic.metadata && topic.metadata.workspaceCategory) {
            topic.metadata.workspaceCategory[0] = topic.name;
            topic.metadata.allowTicketCreationInPC = [AllowTicketCreationInPC.Blocked];
          }
          return Constant.Workspace.WorkspaceCategoryProperty;
        }
      } else {
        let topicWorkspaceCategory = null;
        if (topic.subtopics) {
          topic.subtopics.forEach(subtopic => {
            const unauthTopic = this.isUnauthTopic(subtopic);
            if (unauthTopic) {
              subtopic.metadata.workspaceCategory[0] = topic.name;
              topicWorkspaceCategory = topic.name;
              topic.metadata.allowTicketCreationInPC = [AllowTicketCreationInPC.Blocked];
            }
          });
        }

        if (topicWorkspaceCategory) {
          if (topic.metadata && topic.metadata.workspaceCategory) {
            topic.metadata.workspaceCategory[0] = topicWorkspaceCategory;
          }
          return Constant.Workspace.WorkspaceCategoryProperty;
        }
      }

      return '';
    } else {
      // Check if agent topic wayfinderModelId matches
      let topicHasagentTopics = this.matchWayfinderModelIds(
        topic.metadata.wayfinderModelId,
        Object.keys(Constant.WayfinderModelIds.AgentFlowIds)
      );
      if (topicHasagentTopics) {
        if (topic.subtopics.length < 1) {
          topic.metadata.workspaceCategory[0] =
            Constant.WayfinderModelIds.AgentFlowIds[topic.metadata.wayfinderModelId[0]];
          return Constant.Workspace.WorkspaceCategoryProperty;
        } else {
          let topicWorkspaceCategory = null;
          topic.subtopics.forEach(subtopic => {
            let subtopicHasagentTopics = this.matchWayfinderModelIds(
              subtopic.metadata.wayfinderModelId,
              Object.keys(Constant.WayfinderModelIds.AgentFlowIds)
            );
            if (subtopicHasagentTopics) {
              subtopic.metadata.workspaceCategory[0] =
                Constant.WayfinderModelIds.AgentFlowIds[subtopic.metadata.wayfinderModelId[0]];
              topicWorkspaceCategory = Constant.WayfinderModelIds.AgentFlowIds[subtopic.metadata.wayfinderModelId[0]];
            }
          });

          if (topicWorkspaceCategory) {
            topic.metadata.workspaceCategory[0] = topicWorkspaceCategory;
            return Constant.Workspace.WorkspaceCategoryProperty;
          }
        }
      }

      // topic has no workspace/workspaceCategory, skip
      return '';
    }
  };

  private isUnauthTopic = (topic: SupportTopic): boolean => {
    if (!topic.metadata.workspace.length && !topic.metadata.workspaceCategory.length) {
      let wayfinderModelIds = topic.metadata.wayfinderModelId;
      return wayfinderModelIds.some(id => Constant.WayfinderModelIds.OtherPortalsId.indexOf(id) !== -1);
    }

    return false;
  };

  private addToWorkspaceToProductMapping = (workspaceId: string, productId: string) => {
    if (
      this.state.workspaceToProductMapping.has(workspaceId) &&
      this.state.workspaceToProductMapping.get(workspaceId) !== productId
    ) {
      // Setting workspace mapping to default product family ID,
      // due to edge case scenarios where a workspace maps to more than one product
      if (this.state.workspaceToProductMapping.get(workspaceId) !== this.productFamilyId) {
        this.state.workspaceToProductMapping.set(workspaceId, this.productFamilyId);
      }
    } else {
      this.state.workspaceToProductMapping.set(workspaceId, productId);
    }
  };

  private getServiceHealth = async () => {
    let services = new Map<string, ServiceHealthV2>();
    let filter: ServiceHealthFilter = { onlyVisible: true, productFamilyId: '' };
    await this.partnerSupportService
      .getServiceHealthList()
      .then(apiResponse => {
        let response = apiResponse as HttpResponse<ServiceHealthCDNResponse>;
        for (let service of response.data.Data) {
          for (let serviceChild of service.children) {
            if (serviceChild.status !== HealthStatus.Good.Code) {
              let mappedServiceChild: ServiceHealthV2 = {
                id: serviceChild.id,
                name: serviceChild.name,
                status: this.getServiceHealthStatusByCode(serviceChild.status),
                statusDescription: serviceChild.statusDescription,
                children: this.getServiceHealthChildrenMappedModel(serviceChild.children),
                workspaces: serviceChild.workspaces,
                visible: serviceChild.visible,
              };
              services.set(serviceChild.id, mappedServiceChild);
            }
          }
        }
        this.setState({ activeIncidentServiceHealth: services });
      })
      .catch(error => {
        this.setState({ activeIncidentServiceHealth: services });
      });
  };

  private getServiceHealthChildrenMappedModel(children: ServiceHealthModelV1[]): ServiceHealthV2[] {
    if (children && children.length > 0) {
      return children.map((child): ServiceHealthV2 => {
        return {
          id: child.id,
          name: child.name,
          status: this.getServiceHealthStatusByCode(child.status),
          statusDescription: child.statusDescription,
          children: this.getServiceHealthChildrenMappedModel(child.children),
          workspaces: child.workspaces,
          visible: child.visible,
        } as ServiceHealthV2;
      });
    } else {
      return [];
    }
  }

  private getServiceHealthStatusByCode(status: number): string {
    return status === HealthStatus.Good.Code
      ? HealthStatus.Good.Key
      : status === HealthStatus.Information.Code
      ? HealthStatus.Information.Key
      : status === HealthStatus.Warning.Code
      ? HealthStatus.Warning.Key
      : HealthStatus.Error.Key;
  }

  private getActiveTopicIncident = (issueTypeId: string): ServiceHealthV2 | undefined => {
    if (!Helper.isEmptyOrSpaces(issueTypeId) && this.state.flatTopicTree && this.state.flatTopicTree.length > 0) {
      let selectedTopic = this.state.flatTopicTree.find(x => x.topicId === issueTypeId);
      if (selectedTopic) {
        let activeIncident = this.state.activeIncidentServiceHealth.get(selectedTopic.L2.id);

        return activeIncident;
      }
    }
  };

  private LoadScopingSolutions = async (issueType: SupportTopic) => {
    let topicId: string | undefined;
    if (issueType.id) {
      topicId = issueType.id;
    }
    if (topicId) {
      this.latestSolutionRequestTopicId = topicId;
      await this.partnerSupportService
        .getScopingSolutionsData(topicId)
        .then((response: HttpResponse<SelfHelpSolution[]>) => {
          if (this.latestSolutionRequestTopicId !== topicId) {
            return;
          }
          if (response.data && response.data.length > 0) {
            this.setState({ scopingSolution: response.data[0] }, () => this.loadScopingSolutionsCallback(issueType));
            tracingService.traceEvent(Constant.actionTypes.unAuthLoadedApolloSolutionsEvent, {
              response: response.data,
            });
          } else {
            this.setState({ scopingSolution: null }, () => this.loadScopingSolutionsCallback(issueType));
            tracingService.traceEvent(Constant.actionTypes.unAuthFailedToLoadApolloSolutionEvent, {
              response: response.data,
            });
          }
        })
        .catch(error => {
          if (this.latestSolutionRequestTopicId !== topicId) {
            return;
          }
          this.setState({ scopingSolution: null }, () => this.loadScopingSolutionsCallback(issueType));
          tracingService.traceEvent(Constant.actionTypes.unAuthFailedToLoadApolloSolutionEvent, {
            topicId,
            error: error,
          });
        });
    }
  };

  private onApolloNoSolution = (topicId: string, errorMessages?: string[]) => {
    if (this.state.issueType.id !== topicId || this.state.screenState !== CreateState.Solutions) {
      return;
    }
    tracingService.traceEvent(Constant.actionTypes.unAuthFailedToLoadApolloSolutionEvent, { topicId, errorMessages });
    this.setState({ scopingSolution: null }, () => this.loadScopingSolutionsCallback());
  };

  private setConfirmationState = async (responseTicketId: string, serviceRequest: ServiceRequestV1) => {
    this.setState({ showCreateTicketError: '' });
    let message = {
      ticketId: responseTicketId,
      ticketTitle: serviceRequest.title,
      supportTopicId: serviceRequest.supportTopicId,
      verbatim: this.state.verbatim,
      isTitleSameWithVerbatim: serviceRequest.title == this.state.verbatim ? 'true' : 'false',
      selectedRecommendedResult: this.state.selectedRecommendedResult.toString(),
    };
    tracingService.traceEvent(Constant.actionTypes.createTicket, message, EventGroupType.ticketCreation);
    sessionContext.resetTicketTrackingId();
    // set TempTicket state if ticket is temp ticket & starts with "tmp-"
    // String.startsWith not supported in IE, hence using indexof
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/startsWith
    if (serviceRequest.id && serviceRequest.id.indexOf('tmp-') === 0) {
      this.setState({ isTempTicket: true });
    }

    this.setState({ serviceRequest: serviceRequest, screenState: CreateState.Confirmation, sendingRequest: false });
    this.setConfirmationRoute();
  };

  private createTicket = async (
    description: string,
    contact: Contact,
    workspaceId: string,
    title: string,
    attachments: FileInfo[],
    templateData: object,
    additionalInfo: AdditionalInfo[],
    countryCode: string,
    agentLocale: string,
    emailCcList: string[]
  ) => {
    let serviceRequest: ServiceRequestV1 = {
      id: '',
      primaryContact: {
        firstName: undefined,
        lastName: undefined,
        email: '',
        phoneNumber: '',
      },
      title: '',
      description: '',
      supportTopicName: '',
      severity: ServiceRequestSeverity.Unknown,
      fileLinks: [],
      emailCcList: [],
      newNote: { text: '' },
    };

    serviceRequest.title = title;
    serviceRequest.description = description;
    serviceRequest.supportTopicId = this.state.issueType.id;
    serviceRequest.additionalInfo = additionalInfo;
    serviceRequest.countryCode = countryCode;
    serviceRequest.emailCcList = emailCcList;

    // TODO: [RADAND] need to remove the newNote text after API changes comeplte for addtionalinfo
    if (templateData && serviceRequest.newNote) {
      serviceRequest.newNote.text = JSON.stringify(
        templateData,
        function (key, val) {
          if (key !== 'PartnerAccountInfo') return val;
        },
        1
      );
    }

    serviceRequest.severity = ServiceRequestSeverity.Minimal;
    serviceRequest.primaryContact = contact;
    let dtmLink = Constant.DtmUrl + workspaceId;
    let fileLinks: FileInfo[] = new Array<FileInfo>();
    if (attachments && attachments.length > 0) {
      for (let attachment of attachments) {
        let fileLink: FileInfo = {};
        fileLink.comment = dtmLink;
        fileLink.fileNameWithoutExtension = attachment.fileNameWithoutExtension;
        fileLink.fileSize = attachment.fileSize;
        fileLink.id = Guid.newGuid();
        fileLink.location = dtmLink;
        fileLinks.push(fileLink);
      }
    }
    serviceRequest.fileLinks = fileLinks;
    this.setState({ sendingRequest: true });
    let createPromise = null;

    // UnAuth Flow Create
    await PartnerSupportService.getInstance()
      .getXsrfToken()
      .then(async response => {
        if (response && response.headers && response.headers['x-xsrf-token'] && response.headers['x-xsrf-cookie']) {
          let xsrfRequestToken = response.headers['x-xsrf-token'];
          let xsrfCookieToken = response.headers['x-xsrf-cookie'];

          createPromise = PartnerSupportService.getInstance().createSupportTicketUnauth(
            serviceRequest,
            agentLocale,
            xsrfRequestToken,
            xsrfCookieToken
          );

          await createPromise
            .then(async createPromiseResponse => {
              let response = createPromiseResponse as HttpResponse<ServiceRequest>;
              let responseTicketId = response.data.id;
              serviceRequest.id = response.data.id;
              serviceRequest.supportTopicName = this.state.issueType.name;
              serviceRequest.createdDate = new Date().toISOString();
              serviceRequest.lastModifiedDate = new Date().toISOString();
              this.setState({ showAttachmentError: false });
              if (workspaceId) {
                // remember to await here for patch to finish before setting confirmation state
                // if not, sometimes get attachment call might happen first on confirmation page
                // that will create workspace for this ticket, then patch will fail with 409 (workspace already exist)
                await PartnerSupportService.getInstance()
                  .patchSupportTicket(response.data.id, workspaceId, xsrfRequestToken, xsrfCookieToken)
                  .then(response => {})
                  .catch(error => {
                    // if it is not this scenario: there is only one attachment and the attachment is scoping template or there is no attachment, it means user has attached a file.
                    // We will display attachment error message on confirmation page
                    // file name check cannot use ==, need to use index of because some times file name contains '.' at the end, TODO: find out why

                    if (
                      !(
                        !attachments ||
                        (attachments.length === 1 &&
                          attachments[0].fileNameWithoutExtension?.indexOf(Constant.PartnerSupportDataFileName) != -1)
                      )
                    ) {
                      this.setState({ showAttachmentError: true });
                    }
                  });
              }

              await this.setConfirmationState(responseTicketId ?? '', serviceRequest);
            })
            .catch((error: { request?: { status?: number } }) => {
              if (error.request?.status === 429) {
                this.setState({
                  screenState: CreateState.Details,
                  sendingRequest: false,
                  showCreateTicketError: CreateRequestsExceededError_text,
                });
              } else {
                this.setState({
                  screenState: CreateState.Details,
                  sendingRequest: false,
                  showCreateTicketError: CannotSubmitRequestError_text,
                });
              }
            });
        } else {
          this.setState({
            screenState: CreateState.Details,
            sendingRequest: false,
            showCreateTicketError: CannotSubmitRequestError_text,
          });
        }
      })
      .catch(() => {
        this.setState({
          screenState: CreateState.Details,
          sendingRequest: false,
          showCreateTicketError: CannotSubmitRequestError_text,
        });
      });
  };

  protected goToListPageWithState = (state: object) => {
    this.props.history.push(
      '/' + sessionContext.Locale + '/' + sessionContext.VirtualDirectoryRoot + '/' + Constant.Routes.ServiceRequests,
      state
    );
  };

  protected goToListPage = () => {
    let queryString = '';
    if (!Helper.isEmptyOrSpaces(this.partnerTenantId)) {
      queryString = '?tid=' + this.partnerTenantId;
    }
    if (!Helper.isEmptyOrSpaces(this.msxSellerId)) {
      queryString = '?msxsellerid=' + this.msxSellerId;
    }
    let isPreviewQueryString = '';
    let queryParams = QueryStringParserService.getQueryParameters(window.location.search);
    if (
      queryParams[Constant.QueryStringParams.isPreview] &&
      queryParams[Constant.QueryStringParams.isPreview].toLowerCase() === 'true'
    ) {
      isPreviewQueryString = (Helper.isEmptyOrSpaces(queryString) ? '?' : '&') + 'isPreview=true';
    }
    queryString += isPreviewQueryString;
    this.props.history.push(
      '/' +
        sessionContext.Locale +
        '/' +
        sessionContext.VirtualDirectoryRoot +
        '/' +
        this.serviceRequestRoute +
        queryString
    );
  };

  shouldBlockCreation(): boolean {
    if (this.state.issueType && this.state.issueType.categories) {
      return (
        this.state.issueType?.categories?.map(category => category.toLowerCase()).indexOf(Constant.blockCreation) > -1
      );
    }

    return false;
  }

  private goToAuthCreate = () => {
    Helper.setPublicDeepLinkInfo(
      '',
      SupportPanelState.basic,
      this.state.verbatim,
      sessionContext.TicketTrackingId,
      browserStorage,
      tracingService
    );
    const url = new URL('/dashboard/v2/support/' + this.createRoute, window.location.origin);
    url.search = window.location.search;
    url.searchParams.set('ticketTrackingId', sessionContext.TicketTrackingId);
    window.location.href = url.href;
  };

  private selectNewIssue = (actionType: string, eventGroupType: EventGroupType) => {
    this.setState({
      selectedCategoryOrWorkspace: '',
      supportTopicsCategory2: null,
    });
    tracingService.traceEvent(actionType, {}, eventGroupType);
    this.setBasicsState();
  };

  private selectSolutions = () => {
    let eventName = Constant.actionTypes.detailsReviewSolutions;
    tracingService.traceEvent(eventName, {}, EventGroupType.solutions);
    this.setSolutionsState(this.state.issueType);
  };

  protected cancelCreateTicketSolutionStep = () => {
    let eventName = Constant.actionTypes.solutionCancel;
    tracingService.traceEvent(eventName, {}, EventGroupType.solutions);
    this.setBasicsState();
  };
  protected continueSolutionStep = async () => {
    let eventName = Constant.actionTypes.solutionsContinue;
    tracingService.traceEvent(eventName, {}, EventGroupType.solutions);
    await this.setDetailsState(this.state.issueType);
  };

  private isInternalSupportRoute() {
    return window.location.pathname.indexOf('internal/tools/support') !== -1;
  }

  private isInvalidInternalRoute() {
    if (this.isInternalSupportRoute()) {
      if (this.productFamilyId == ProductFamilies.MSX) {
        return Helper.isEmptyOrSpaces(this.msxSellerId);
      } else {
        return Helper.isEmptyOrSpaces(this.partnerTenantId) && Helper.isEmptyOrSpaces(this.partnerMpnId);
      }
    }

    return false;
  }

  private getPageTitleDiv = (): JSX.Element => {
    if (this.state.screenState == CreateState.Basics && this.isSupportPublicRoute) {
      // TODO: illustration picture
      const imgPath = require('./img/Illustration.jpg');
      return (
        <div className="col-xs-24 flexShrink0" style={{ margin: '24px 0px 24px 0px', padding: '0px 24px' }}>
          <div className="cardView" style={{ padding: '24px' }}>
            <div className="row">
              <div className="col-xs-14">
                <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', marginTop: '24px' }}>
                  {AllPartnerCenterHelpInOnePlace_text}
                </div>
                <div style={{ marginBottom: '25px' }}>{CreateOnlineTicket_text}</div>
                <button
                  className="btn-primary"
                  style={{ whiteSpace: 'normal', marginBottom: '40px' }}
                  onClick={this.goToAuthCreate}
                >
                  {CreateTicketInPC_text}
                </button>
              </div>
              <div className="col-xs-10">
                <img alt="" className="fit-image" src={String(imgPath)} />
              </div>
            </div>
          </div>
        </div>
      );
    }
    let pageTitle = '';
    switch (this.state.screenState) {
      case CreateState.Basics: {
        pageTitle = resx.getLocalizedResource('HelpAndSupport_text');
        break;
      }
      case CreateState.Solutions: {
        pageTitle = resx.getLocalizedResource('Recommended_Solutions');
        break;
      }
      case CreateState.Details: {
        pageTitle = resx.getLocalizedResource('CreateSupportTicket_text');
        break;
      }
      case CreateState.Confirmation: {
        pageTitle = resx.getLocalizedResource('CreateSupportTicketConfirmation_text');
        break;
      }
    }
    let actionType = '';
    let eventGroupType = EventGroupType.fullPage;
    if (this.state.screenState == CreateState.Solutions) {
      actionType = Constant.actionTypes.solutionSelectDifferentIssue;
      eventGroupType = EventGroupType.solutions;
    } else if (this.state.screenState == CreateState.Details) {
      actionType = Constant.actionTypes.detailsSelectDifferentIssue;
      eventGroupType = EventGroupType.ticketCreation;
    }
    let titleDiv = <></>;
    if (this.state.screenState !== CreateState.Confirmation) {
      titleDiv = (
        <div className={this.titleClassName}>
          <div className={this.supportHeaderClassName}>
            <header>
              <h1 className="page-title" style={{ fontSize: '20px', fontWeight: 800, lineHeight: '28px' }}>
                {pageTitle}
              </h1>
            </header>
            {(this.state.screenState == CreateState.Solutions || this.state.screenState == CreateState.Details) && (
              <div>
                <span className="headerTopicLabel">
                  {!Helper.isEmptyOrSpaces(this.state.topicLabel) ? this.getTopicLabel() : ''}
                </span>
                {!Helper.isEmptyOrSpaces(this.state.topicLabel) &&
                  (this.state.screenState == CreateState.Solutions ||
                    this.state.screenState == CreateState.Details) && (
                    <a
                      href="#"
                      data-toggle="tab"
                      style={{
                        fontSize: '14px',
                        paddingLeft: '10px',
                        paddingTop: 0,
                        paddingBottom: 0,
                        position: 'relative',
                        display: 'inline-block',
                      }}
                      onClick={e => this.selectNewIssue(actionType, eventGroupType)}
                    >
                      {Select_Different_Issue}
                    </a>
                  )}
                {this.state.screenState == CreateState.Details &&
                  this.state.scopingSolution != null && (
                    <a
                      href="#"
                      className="btn-link"
                      data-toggle="tab"
                      style={{
                        fontSize: '14px',
                        paddingLeft: '10px',
                        paddingTop: 0,
                        paddingBottom: 0,
                        position: 'relative',
                        display: 'inline-block',
                      }}
                      onClick={this.selectSolutions}
                    >
                      {ReviewSolution_text}
                    </a>
                  )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return titleDiv;
  };

  private getTopicLabel = () => {
    if (this.state.topicLabel.startsWith(' > ')) {
      const updatedTopicLabel = this.state.topicLabel.replace(' > ', '');
      this.setState({ topicLabel: updatedTopicLabel });
      return updatedTopicLabel;
    }

    return this.state.topicLabel;
  };

  public updateServiceRequest = (serviceRequest: ServiceRequestV1) => {
    let alert = '';

    if (serviceRequest.newNote && Helper.isEmptyOrSpaces(serviceRequest.newNote.text)) {
      serviceRequest.newNote = undefined;
    }
    this.setState({ loading: true });
    PartnerSupportService.getInstance()
      .updateServiceRequestV1(serviceRequest)
      .then(() => {
        this.goToListPage();
      })
      .catch((error: any) => {
        alert = resx.getLocalizedResource('AllView_ErrorMessage');
        let action = { actionType: Constant.TraceTypes.TrackEvent, tags: Constant.actionTypes.clientSideProblem };
        tracingService.traceEvent(Constant.actionTypes.clientSideProblem, action);
        this.setState({ loading: false });
      });
  };

  private getTicketDetailsBannerDiv = () => {
    if (this.state.isTempTicket) {
      return (
        <WebCoreInformationBannerView
          title={TemporarySupportRequestBannerTitle_text}
          text={
            this.isInternalSupportRoute() ? TemporaryAgentSupportRequestBanner_text : TemporarySupportRequestBanner_text
          }
        />
      );
    }
    if (
      this.state.showAttachmentError &&
      this.state.serviceRequest &&
      !Helper.isEmptyOrSpaces(this.state.serviceRequest.id ?? '')
    ) {
      let attachmentErrorBannerText = TryAddAttachmentOnDetailsPage_text.replace(
        '{0}',
        '/' +
          sessionContext.Locale +
          '/' +
          sessionContext.VirtualDirectoryRoot +
          '/' +
          Constant.Routes.ServiceRequests +
          '/' +
          this.state.serviceRequest.id +
          window.location.search
      );
      return <WebCoreErrorBannerView title={WeWerentAbleToSaveYourAttachment_text} text={attachmentErrorBannerText} />;
    } else {
      return null;
    }
  };
  public hideAlert = () => {
    return '';
  };

  public render() {
    let reviewSolutionText = '';
    if (this.state.issueType && this.state.issueType.id) {
      reviewSolutionText = ReviewSolution_text + ' >';
    }
    if (this.isStepRequireAuth('Solution', this.state.issueType)) {
      reviewSolutionText = SigninToPCToReviewSolution_text;
    }
    let statusOptions = [
      { id: Constant.TicketStatus.open, name: resx.getLocalizedResource('AllView_open'), selected: true },
      { id: Constant.TicketStatus.closed, name: resx.getLocalizedResource('AllView_closed') },
    ];
    let solutionNextStepDiv = null;
    if (!this.isContinueDisabled()) {
      solutionNextStepDiv = this.getSolutionNextStepDiv(
        this.state.issueType,
        this.state.serviceRequestsWithTopicId ?? []
      );
    }
    let confirmationTextBox = null;
    let ticketDetailsBanner = <div className="flexShrink0">{this.getTicketDetailsBannerDiv()}</div>;
    if (this.state.screenState === CreateState.Confirmation) {
      let confirmationText;
      confirmationText = resx
        .getLocalizedResource('CreateSupportTicketConfirmationBody_text')
        .replace('{0}', this.state.serviceRequest?.id ?? '');

      //If ticket is temp Ticket we hide the ticket confirmation.
      if (!this.state.isTempTicket) {
        confirmationTextBox = (
          <div className="display-inline remove-all-padding">
            <AlertBanner
              alertType={AlertType.success}
              title={resx.getLocalizedResource('CreateSupportTicketConfirmation_text')}
              body={confirmationText}
              onClose={this.hideAlert}
            />
          </div>
        );
      }
    }

    let basicsDiv =
      this.state.screenState === CreateState.Basics ? (
        <TopicsContainer
          supportTopicsTree={this.state.supportTopicsTree}
          selectTopic={this.selectTopic}
          setSolutionState={this.setSolutionsState}
          filteredTopicsTree={this.state.filteredTopicsTree}
          selectedCategoryOrWorkspace={this.state.selectedCategoryOrWorkspace}
          supportTopicsCategory2={this.state.supportTopicsCategory2 ?? ''}
          setSupportTopicsWorkspace={this.setSupportTopicsWorkspace}
          setSupportTopicsCategory={this.setSupportTopicsCategory}
          setSupportTopicsCategory2={this.setSupportTopicsCategory2}
          reviewSolutionText={reviewSolutionText}
          flatTopicTree={this.state.flatTopicTree ?? []}
          isSupportPublicRoute={this.isSupportPublicRoute}
          category1To2map={this.state.category1To2map}
          clearSearch={this.setClearSearch}
          topicDropdownActionCount={this.state.topicDropdownActionCount}
          topicSearchAttemptCount={this.state.topicSearchAttemptCount}
          incrementTopicDropdownActionCount={this.incrementTopicDropdownActionCount}
          incrementTopicSearchAttemptCount={this.incrementTopicSearchAttemptCount}
          topicCategoriesOrWorkspaces={this.state.topicCategoriesOrWorkspaces ?? []}
          topSupportTopicsList={this.state.topSupportTopicList ?? []}
          verbatim={Helper.isEmptyOrSpaces(this.state.ticketTitle) ? this.state.verbatim : this.state.ticketTitle}
          updateVerbatim={(verbatim: string) => {
            this.setState({ verbatim: verbatim, ticketTitle: '' });
          }}
          workspaceView={this.workspaceView}
          workspaceDropdownEnabled={this.state.workspaceDropdownEnabled ?? true}
          includeUnauthTopics={this.state.includeUnauthTopics}
          otherWorkspaces={this.state.otherWorkspaces}
          productFamilyId={this.productFamilyId}
          workspaceToProductMapping={this.state.workspaceToProductMapping}
          agentTopics={this.state.agentTopics}
        />
      ) : null;

    let detailsDiv =
      this.state.screenState == CreateState.Details ? (
        <NewFormsContainer
          issueType={this.state.issueType}
          issueTitle={this.state.issueTitle}
          categoryOrWorkspace={this.state.selectedCategoryOrWorkspace}
          cancel={this.cancel}
          submitRequest={this.createTicket}
          sendingRequest={this.state.sendingRequest}
          showCreateTicketError={this.state.showCreateTicketError ?? ''}
          isSupportPublicRoute={this.isSupportPublicRoute}
          partnerTenantId={this.partnerTenantId}
          verbatim={this.state.verbatim}
          updateTicketTitle={(ticketTitle: string) => {
            this.setState({ ticketTitle: ticketTitle });
          }}
          workspaceView={this.workspaceView}
          msxSellerId={this.msxSellerId}
        />
      ) : null;
    let solutionsDiv =
      this.state.screenState == CreateState.Solutions ? (
        <SolutionsView
          serviceHealthInfo={this.state.serviceHealthInfo ?? []}
          activeTopicIncident={this.getActiveTopicIncident(this.state.issueType.id)}
          continue={this.setDetailsState}
          issueType={this.state.issueType}
          issueTitle={this.state.issueTitle}
          scopingSolution={this.state.scopingSolution}
          onApolloNoSolution={this.onApolloNoSolution}
          solutionBackButton={this.getSolutionBackButton()}
          solutionNextStepDiv={solutionNextStepDiv ?? <></>}
          workspaceView={this.workspaceView}
        />
      ) : null;
    let confirmationDiv =
      this.state.screenState === CreateState.Confirmation ? (
        <TicketDetails
          serviceRequest={this.state.serviceRequest}
          workspaceId={''}
          customerOrganizationId={''}
          statusOptions={statusOptions}
          isPartnerTicket={true}
          isRaveTicket={false}
          isAzureTicket={() => {
            return false;
          }}
          updateServiceRequest={this.isSupportPublicRoute ? () => {} : this.updateServiceRequest}
          cancelHandler={this.cancel}
          isSupportPublicRoute={this.isSupportPublicRoute}
          titleDiv={this.getPageTitleDiv()}
          confirmationTextBox={confirmationTextBox}
          ticketDetailsBanner={ticketDetailsBanner}
          okButtonText={ReturnToTicketList_text}
          issueType={this.state.issueType}
        />
      ) : null;
    let display = null;
    if (this.isInvalidInternalRoute()) {
      const errorMessage =
        this.productFamilyId == ProductFamilies.MSX
          ? resx.getLocalizedResource('MsxSellerIdMissingError_text')
          : resx.getLocalizedResource('PartnerTenantIdMissingError_text');

      display = (
        <div className="margintop10 win-color-fg-secondary" style={{ padding: '0px 24px' }}>
          {errorMessage}
        </div>
      );
    } else {
      switch (this.state.screenState) {
        case CreateState.Basics: {
          display = basicsDiv;
          break;
        }
        case CreateState.Solutions: {
          display = solutionsDiv;
          break;
        }
        case CreateState.Details: {
          display = detailsDiv;
          break;
        }
        case CreateState.Confirmation: {
          display = confirmationDiv;
          break;
        }
      }
    }

    if (this.workspaceView) {
      const workspaceDisplay = (
        <div>
          {this.getPageTitleDiv()}
          {display}
        </div>
      );

      return (
        <div className="spacer-sm-bottom">
          <LoadingContainer
            showProgress={this.state.loading || this.state.checkingIfShouldRedirect}
            display={workspaceDisplay}
          ></LoadingContainer>
        </div>
      );
    } else {
      const pageViewDisplay = (
        <PageWrapperView
          titleDiv={this.getPageTitleDiv()}
          overflowY={this.pageWrapperOverflowY}
          paddingTop={this.pageWrapperPaddingTop}
          paddingBottom={''}
        >
          {display ?? <></>}
        </PageWrapperView>
      );

      return (
        <div className={this.containerOuterClass} style={{ height: '100%', margin: '0' }} role="main">
          <div
            className="col-xs-24 flexWrapper rightPanelDetailsView generalBackground"
            style={this.containerInnerStyle}
          >
            <LoadingContainer
              showProgress={this.state.loading || this.state.checkingIfShouldRedirect}
              display={pageViewDisplay}
            ></LoadingContainer>
          </div>
        </div>
      );
    }
  }
}
